import { Plus, Users } from "lucide-react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";

import type { DuplicateMatch } from "../../../api/types/clientDirectory";
import { duplicateSummary } from "./createModel";

type Props = {
  matches: DuplicateMatch[];
  onDismiss: (id: string) => void;
};

/** "May already be your client", with a way into the existing record. */
export const DuplicateWarning: FC<Props> = ({ matches, onDismiss }) => {
  const navigate = useNavigate();
  const [match, ...others] = matches;
  if (!match) return null;
  const id = match.foreign_national_id;
  return (
    <div
      className="flex flex-col gap-2.5 rounded-lg border border-warn-line bg-warn-soft p-3"
      role="status"
      data-testid="duplicate-warning"
    >
      <div className="flex items-start gap-2.5">
        <Users className="mt-0.5 size-4 shrink-0 text-warn" />
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-base font-medium text-warn">
            {match.name} may already be your client
          </span>
          <span className="text-sm text-fg-2">
            {duplicateSummary(match)}
            {others.length > 0
              ? ` · ${others.length} more possible match${
                  others.length === 1 ? "" : "es"
                }`
              : ""}
          </span>
        </div>
      </div>
      <div className="ml-6 flex flex-wrap gap-1.5">
        <Button
          type="button"
          size="xs"
          variant="secondary"
          onClick={() => navigate(`/clients/${id}`)}
        >
          Open {match.name}
        </Button>
        <Button
          type="button"
          size="xs"
          variant="secondary"
          onClick={() => navigate(`/clients/${id}?tab=cases&newCase=1`)}
        >
          <Plus /> Start a New Case for Them
        </Button>
        <Button
          type="button"
          size="xs"
          variant="ghost"
          onClick={() => onDismiss(id)}
        >
          It&apos;s a Different Person
        </Button>
      </div>
    </div>
  );
};
